import { useSyncExternalStore } from 'react'

/**
 * Global database-outage signal (SYSTEM_ARCHITECTURE §13): when the API
 * answers 503 DB_UNAVAILABLE / 504 DB_TIMEOUT the shell shows a read-only
 * banner — cached queries keep rendering, mutations queue offline. The flag
 * self-clears after a quiet minute or on the next successful response.
 */

const OUTAGE_WINDOW_MS = 60_000

let outageUntil = 0
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

export function noteDbOutage(): void {
  outageUntil = Date.now() + OUTAGE_WINDOW_MS
  emit()
}

export function clearDbOutage(): void {
  if (outageUntil !== 0) {
    outageUntil = 0
    emit()
  }
}

/** Inspect a response for the DB-outage codes; safe to call on every fetch. */
export async function trackDbOutage(res: Response): Promise<void> {
  if (res.ok) {
    clearDbOutage()
    return
  }
  if (res.status !== 503 && res.status !== 504) return
  try {
    const body = (await res.clone().json()) as { error?: { code?: string } }
    if (body.error?.code === 'DB_UNAVAILABLE' || body.error?.code === 'DB_TIMEOUT') {
      noteDbOutage()
    }
  } catch {
    // Non-JSON 503 (gateway page) — not a classified DB outage.
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function snapshot(): boolean {
  return outageUntil > Date.now()
}

export function useDbOutage(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false)
}
